/**
 * DriverReportIssue - Combined "Signaler" tab
 *
 * Two modes:
 * - "maintenance" → Quick maintenance request (brakes, engine, tires...)
 * - "claim" → Declare an accident/incident
 *
 * Both create backend records in draft/submitted status for manager review.
 */
import React, { useState } from 'react';
import { Wrench, Shield, CheckCircle, AlertCircle, ChevronLeft } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';

const MAINTENANCE_TYPES = [
  { value: 'brake_service', label: 'Freins' },
  { value: 'engine_repair', label: 'Moteur' },
  { value: 'tire_rotation', label: 'Pneus' },
  { value: 'oil_change', label: 'Vidange / Huile' },
  { value: 'electrical', label: 'Électrique' },
  { value: 'bodywork', label: 'Carrosserie' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'emergency', label: 'Urgence (immobilisé)' },
  { value: 'other', label: 'Autre' },
];

const PRIORITIES = [
  { value: 'low', label: 'Basse', color: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300' },
  { value: 'medium', label: 'Moyenne', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  { value: 'high', label: 'Haute', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { value: 'critical', label: 'Critique', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
];

const CLAIM_TYPES = [
  { value: 'collision', label: 'Collision' },
  { value: 'theft', label: 'Vol' },
  { value: 'breakdown', label: 'Panne' },
  { value: 'glass_breakage', label: 'Bris de glace' },
  { value: 'fire', label: 'Incendie' },
  { value: 'hail', label: 'Grêle' },
  { value: 'bodily_injury', label: 'Dommage corporel' },
  { value: 'other', label: 'Autre' },
];

function MaintenanceForm({ onSuccess }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'other',
    priority: 'medium',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError('Le titre et la description sont obligatoires');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await driverMonoAPI.requestMaintenance(form);
      onSuccess('maintenance');
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Type de problème</label>
        <select
          value={form.type}
          onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
          className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {MAINTENANCE_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Titre *</label>
        <input
          type="text"
          value={form.title}
          onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Ex: Bruit de freins à l'arrière"
          className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
          maxLength={200}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Description *</label>
        <textarea
          value={form.description}
          onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Décrivez le problème en détail..."
          rows={3}
          className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Urgence</label>
        <div className="flex gap-2">
          {PRIORITIES.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, priority: p.value }))}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                form.priority === p.value
                  ? p.color + ' ring-2 ring-offset-1 ring-blue-400 dark:ring-offset-slate-800'
                  : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-blue-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? 'Envoi...' : 'Envoyer la demande'}
      </button>
    </form>
  );
}

function ClaimForm({ onSuccess }) {
  const [form, setForm] = useState({
    type: 'collision',
    event_date: new Date().toISOString().split('T')[0],
    event_time: '',
    location: '',
    description: '',
    drivable: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type || !form.event_date) {
      setError('Le type et la date sont obligatoires');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await driverMonoAPI.declareClaim(form);
      onSuccess('claim');
    } catch (err) {
      setError(err.message || 'Erreur lors de la déclaration');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Type de sinistre *</label>
        <select
          value={form.type}
          onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
          className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {CLAIM_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Date *</label>
          <input
            type="date"
            value={form.event_date}
            onChange={e => setForm(prev => ({ ...prev, event_date: e.target.value }))}
            className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Heure</label>
          <input
            type="time"
            value={form.event_time}
            onChange={e => setForm(prev => ({ ...prev, event_time: e.target.value }))}
            className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Lieu</label>
        <input
          type="text"
          value={form.location}
          onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
          placeholder="Ex: Rue de la Paix, Paris"
          className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Description</label>
        <textarea
          value={form.description}
          onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Décrivez les circonstances..."
          rows={3}
          className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Le véhicule peut-il rouler ?</label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setForm(prev => ({ ...prev, drivable: true }))}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              form.drivable
                ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/20 dark:border-green-700 dark:text-green-400'
                : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600'
            }`}
          >
            Oui, roulant
          </button>
          <button
            type="button"
            onClick={() => setForm(prev => ({ ...prev, drivable: false }))}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              !form.drivable
                ? 'bg-red-50 border-red-300 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400'
                : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600'
            }`}
          >
            Non, immobilisé
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-red-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? 'Envoi...' : 'Déclarer le sinistre'}
      </button>
    </form>
  );
}

export default function DriverReportIssue() {
  const [mode, setMode] = useState(null); // null = picker, 'maintenance' | 'claim'
  const [success, setSuccess] = useState(null); // 'maintenance' | 'claim'

  const handleSuccess = (type) => {
    setSuccess(type);
    setMode(null);
  };

  const handleReset = () => {
    setSuccess(null);
    setMode(null);
  };

  // Success confirmation
  if (success) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-8 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 dark:text-green-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {success === 'maintenance' ? 'Demande envoyée' : 'Sinistre déclaré'}
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
          {success === 'maintenance'
            ? 'Votre demande de maintenance a été transmise à votre gestionnaire.'
            : 'Votre déclaration de sinistre a été enregistrée. Votre gestionnaire sera notifié.'}
        </p>
        <button
          onClick={handleReset}
          className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
        >
          Nouveau signalement
        </button>
      </div>
    );
  }

  // Form view
  if (mode) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setMode(null)}
          className="flex items-center gap-1 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour
        </button>
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {mode === 'maintenance' ? 'Demande de maintenance' : 'Déclaration de sinistre'}
          </h3>
          {mode === 'maintenance' ? (
            <MaintenanceForm onSuccess={handleSuccess} />
          ) : (
            <ClaimForm onSuccess={handleSuccess} />
          )}
        </div>
      </div>
    );
  }

  // Mode picker
  return (
    <div className="space-y-4">
      <button
        onClick={() => setMode('maintenance')}
        className="w-full bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5 flex items-center gap-4 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow transition-all text-left"
      >
        <div className="w-12 h-12 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
          <Wrench className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Problème mécanique</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            Freins, moteur, pneus, électrique, carrosserie...
          </p>
        </div>
      </button>

      <button
        onClick={() => setMode('claim')}
        className="w-full bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5 flex items-center gap-4 hover:border-red-300 dark:hover:border-red-600 hover:shadow transition-all text-left"
      >
        <div className="w-12 h-12 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
          <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Accident / Sinistre</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            Collision, vol, bris de glace, incendie, panne...
          </p>
        </div>
      </button>
    </div>
  );
}
